import { HttpClient } from '@angular/common/http';
import { Component, OnDestroy, OnInit } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { PageEvent } from '@angular/material/paginator';
import { Subscription } from 'rxjs';
import { DialogComponent } from 'src/app/components/dialog/dialog.component';
import { ListaDadosColuna } from 'src/app/components/lista-dados/lista-dados.component';
import { BePontoEletronicoLog } from 'src/app/models/BePontoEletronicoLog';
import { BeRegistrarPontoResult } from 'src/app/models/BeRegistrarPontoResult';
import { DialogData } from 'src/app/models/dialog-data';
import { SnackBarService } from 'src/app/services/snack-bar.service';
import { ModoExibicaoPersistidoService } from 'src/app/services/modo-exibicao-persistido.service';

type ModoExibicaoPonto = 'historico' | 'agendamentos' | 'calendario' | 'feriados';

interface LogsHistoricoPaginado {
  data?: BePontoEletronicoLog[];
  total?: number;
  totalCount?: number;
  Data?: BePontoEletronicoLog[];
  Total?: number;
  TotalCount?: number;
}

@Component({
  selector: 'app-ponto-eletronico',
  templateUrl: './ponto-eletronico.component.html',
  styleUrls: ['./ponto-eletronico.component.css']
})
export class PontoEletronicoComponent implements OnInit, OnDestroy {
  public logs: BePontoEletronicoLog[] = [];
  public logsHistorico: BePontoEletronicoLog[] = [];
  public subscription?: Subscription;
  private subscriptionCalendario?: Subscription;
  public registrando = false;
  public modoExibicao: ModoExibicaoPonto = 'calendario';
  public paginaHistorico = 0;
  public itensPorPaginaHistorico = 10;
  public totalLogsHistorico = 0;

  private readonly chaveModoExibicao = 'ponto-eletronico.modoExibicao';

  public colunasListaDados: ListaDadosColuna[] = [
    { campo: 'dtCriadoFormatado', rotulo: 'Criado em', tamanho: '160px' },
    { campo: 'sucessoFormatado', rotulo: 'Sucesso', tamanho: '65px' },
    { campo: 'noStatusHttp', rotulo: 'HTTP', tamanho: '65px' },
    { campo: 'dsMensagem', rotulo: 'Mensagem', tamanho: 'calc(100% - 290px)' },
  ];

  public constructor(
    private httpClient: HttpClient,
    private dialog: MatDialog,
    private snackBarService: SnackBarService,
    private modoExibicaoPersistidoService: ModoExibicaoPersistidoService,
  ) {}

  public ngOnInit(): void {
    if (this.modoExibicaoPersistidoService.deveRestaurarModoExibicao()) {
      const modoExibicao = sessionStorage.getItem(this.chaveModoExibicao);
      if (modoExibicao === 'historico' || modoExibicao === 'agendamentos' || modoExibicao === 'calendario' || modoExibicao === 'feriados')
        this.modoExibicao = modoExibicao;
    }
    else
      sessionStorage.setItem(this.chaveModoExibicao, 'calendario');
    this.carregarHistorico();
    this.carregarLogsCalendario();
  }

  public ngOnDestroy(): void {
    this.unsubscribe();
    this.subscriptionCalendario?.unsubscribe();
  }

  public simularRegistro(): void {
    this.registrarPonto(true);
  }

  public registrarPontoReal(): void {
    this.registrarPonto(false);
  }

  public exibirHistorico(): void {
    this.modoExibicao = 'historico';
    sessionStorage.setItem(this.chaveModoExibicao, this.modoExibicao);
  }

  public exibirAgendamentos(): void {
    this.modoExibicao = 'agendamentos';
    sessionStorage.setItem(this.chaveModoExibicao, this.modoExibicao);
  }

  public exibirCalendario(): void {
    this.modoExibicao = 'calendario';
    sessionStorage.setItem(this.chaveModoExibicao, this.modoExibicao);
  }

  public exibirFeriados(): void {
    this.modoExibicao = 'feriados';
    sessionStorage.setItem(this.chaveModoExibicao, this.modoExibicao);
  }

  public onMudouPaginaHistorico(evento: PageEvent): void {
    this.paginaHistorico = evento.pageIndex;
    this.itensPorPaginaHistorico = evento.pageSize;
    this.carregarHistorico();
  }

  public onDelete(log: BePontoEletronicoLog): void {
    if (this.subscription || !log.id) {
      return;
    }

    const dialogData: DialogData = {
      title: 'Alerta',
      message: 'Deseja excluir esse registro?',
      buttonText: 'Não',
      secondButtonText: 'Sim',
    };

    const dialogRef = this.dialog.open(DialogComponent, { data: dialogData });
    dialogRef.afterClosed().subscribe((result) => {
      if (result) {
        this.excluirLog(log);
      }
    });
  }

  private registrarPonto(simular: boolean): void {
    this.unsubscribe();
    this.registrando = true;
    console.log('[PontoEletronico] Iniciando registro de ponto', { simular });

    this.subscription = this.httpClient.post<BeRegistrarPontoResult>(
      '/api/PontoEletronico/registrar-ponto',
      { simular }
    ).subscribe({
      next: (res) => {
        console.log('[PontoEletronico] Registro de ponto finalizado', {
          simular,
          sucesso: res.blSucesso,
          mensagem: res.dsMensagem,
          delayCasa: res.nrDelayCasa,
          delaySegundos: res.nrDelaySegundos,
          delayRepeticoes: res.nrDelayRepeticoes,
          resposta: res,
        });
        this.unsubscribe();
        this.registrando = false;
        this.snackBarService.exibirSucesso(res.dsMensagem ?? 'Processo finalizado.');
        this.carregarHistorico();
      },
      error: (err) => {
        console.log('[PontoEletronico] Erro ao registrar ponto', {
          simular,
          status: err.status,
          statusText: err.statusText,
          erro: err,
        });
        this.unsubscribe();
        this.registrando = false;
        this.snackBarService.exibirErro(`Erro! - ${err.status} - ${err.statusText}`);
        this.carregarHistorico();
      }
    });
  }

  private carregarHistorico(): void {
    this.unsubscribe();
    this.subscription = this.httpClient.get<LogsHistoricoPaginado>(
      `/api/PontoEletronico/logs/paginado?pageIndex=${this.paginaHistorico}&pageSize=${this.itensPorPaginaHistorico}`
    ).subscribe({
      next: (res) => {
        this.logsHistorico = (res.data ?? res.Data ?? []).map(log => ({
          ...log,
          dtCriadoFormatado: this.formatarData(log.dtCriado),
          sucessoFormatado: log.blSucesso ? 'Sim' : 'Não',
        } as any));
        this.totalLogsHistorico = res.totalCount ?? res.total ?? res.TotalCount ?? res.Total ?? this.logsHistorico.length;
        this.unsubscribe();
      },
      error: (err) => {
        this.unsubscribe();
        this.snackBarService.exibirErro(`Erro ao carregar histórico! - ${err.status} - ${err.statusText}`);
      }
    });
  }

  private carregarLogsCalendario(): void {
    this.subscriptionCalendario?.unsubscribe();
    this.subscriptionCalendario = this.httpClient.get<BePontoEletronicoLog[]>('/api/PontoEletronico/logs').subscribe({
      next: (res) => {
        this.logs = res ?? [];
        this.subscriptionCalendario = undefined;
      },
      error: (err) => {
        this.subscriptionCalendario = undefined;
        this.snackBarService.exibirErro(`Erro ao carregar histórico! - ${err.status} - ${err.statusText}`);
      }
    });
  }

  private excluirLog(log: BePontoEletronicoLog): void {
    this.unsubscribe();
    this.subscription = this.httpClient.delete(`/api/PontoEletronico/logs/${log.id}`).subscribe({
      next: () => {
        this.logs = this.logs.filter(item => item.id !== log.id);
        this.logsHistorico = this.logsHistorico.filter(item => item.id !== log.id);
        this.totalLogsHistorico = Math.max(this.totalLogsHistorico - 1, 0);
        this.unsubscribe();
        this.snackBarService.exibirSucesso('Registro excluído!');
      },
      error: (err) => {
        this.unsubscribe();
        this.snackBarService.exibirErro(`Erro ao excluir registro! - ${err.status} - ${err.statusText}`);
      }
    });
  }

  private formatarData(data: Date | string): string {
    if (!data) {
      return '';
    }

    return new Date(data).toLocaleString('pt-BR');
  }

  private unsubscribe(): void {
    if (this.subscription) {
      this.subscription.unsubscribe();
      this.subscription = undefined;
    }
  }
}
